import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { BellIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Disclosure, Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import routes, { fetchSubTopics } from "../Routes/Route";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import LanguageSwitcher from "./LanguageSwitcher"; 

// Helper function to get user initials from name
const getUserInitials = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to get a color based on name (for consistent avatar colors)
const getAvatarColor = (name) => {
  if (!name) return "bg-gray-500";
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export default function StackedLayoutWithSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [subTopics, setSubTopics] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [darkMode] = useState(false);
  const [time, setTime] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Get user data from localStorage
  const [userName, setUserName] = useState(localStorage.getItem('name') || 'User');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('email') || 'user@example.com');
  const [isFormFinished, setIsFormFinished] = useState(false);
  const [userDimensions, setUserDimensions] = useState([]);
  const [dimensionsLoaded, setDimensionsLoaded] = useState(false);
  
  // Update user data when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setUserName(localStorage.getItem('name') || 'User');
      setUserEmail(localStorage.getItem('email') || 'user@example.com');
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    // Also check on mount
    handleStorageChange();

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check if form is finished (for company admins)
  const checkIfFormFinished = async () => {
    try {
      const response = await axios.post('/is-form-finished');
      if (response.data.status) {
        setIsFormFinished(response.data.isFormFinished);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  useEffect(() => {
    // Check form status on mount
    checkIfFormFinished();
    // Fetch user dimensions
    fetchUserDimensions();
    
    // Listen for form status changes (when form is completed)
    const handleFormStatusChange = () => {
      checkIfFormFinished();
    };
    
    window.addEventListener('formStatusChanged', handleFormStatusChange);
    
    // Also check when location changes (in case user navigates after form completion)
    const handleLocationChange = () => {
      if (localStorage.getItem('role') === 'companyAdmin') {
        checkIfFormFinished();
      }
    };
    
    // Check on location change
    handleLocationChange();
    
    return () => {
      window.removeEventListener('formStatusChanged', handleFormStatusChange);
    };
  }, [location]);

  // Listen for storage events to refresh dimensions when assignments might have changed
  useEffect(() => {
    const handleStorageChange = () => {
      // Refresh dimensions when localStorage changes (might indicate assignment changes)
      fetchUserDimensions();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fetch current user's assigned dimensions
  const fetchUserDimensions = async () => {
    try {
      const response = await axios.get('/api/user/dimensions');
      if (response.data.success) {
        const dimensions = response.data.dimensions || [];
        setUserDimensions(dimensions);
        setDimensionsLoaded(true);
      } else {
        setUserDimensions([]);
        setDimensionsLoaded(true);
      }
    } catch (error) {
      setUserDimensions([]);
      setDimensionsLoaded(true);
    }
  };

  // Check if user has 'Environment' dimension
  const hasEnvironmentDimension = () => {
    // If dimensions haven't loaded yet, return false to hide the route
    // This prevents the route from showing before we know the actual dimensions
    if (!dimensionsLoaded) {
      return false;
    }
    
    // If dimensions are loaded but empty, user has no dimensions assigned
    if (userDimensions.length === 0) {
      return false;
    }
    
    return userDimensions.some(dim => {
      if (!dim) return false;
      const dimLower = dim.toLowerCase().trim();
      // Check for exact match or contains 'environment'
      return dimLower === 'environment' || dimLower.includes('environment');
    });
  };

  // Calculate clock hands rotation
  const secondsRotation = (time.getSeconds() / 60) * 360;
  const minutesRotation = (time.getMinutes() / 60) * 360;
  const hoursRotation = ((time.getHours() % 12) / 12) * 360;

  useEffect(() => {
    const loadSubTopics = async () => {
      setLoading(true);
      const topics = await fetchSubTopics();
      setSubTopics(topics);
      setLoading(false);
    };

    loadSubTopics();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-close sidebar on mobile when switching to desktop
      if (!mobile && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('is_first_login');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('isFirstLogin');
    localStorage.removeItem('name');

    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close sidebar when clicking on a link on mobile
  const handleMobileLinkClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className="flex min-h-full w-full max-w-full overflow-x-hidden relative">
      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-screen z-50 flex flex-col overflow-y-auto ${
          darkMode ? "bg-dark text-light" : "bg-white text-dark"
        } transition-all duration-300 ease-in-out ${
          isMobile 
            ? (isSidebarOpen ? "translate-x-0" : "-translate-x-full") 
            : "translate-x-0"
        }`}
        style={{
          width: isMobile ? "280px" : window.innerWidth >= 1920 ? "280px" : "240px",
          scrollbarWidth: "thin",
          scrollbarColor: "#888 transparent",
        }}
      >
        {/* Sidebar Heading */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            <img src="/EcoMina_Favicon.png" alt="EcoMina Logo" className="h-8 w-8 mr-2" />
            <h1 className={`text-xl font-bold tracking-wide ${darkMode ? "text-light" : "text-success"}`}>
              <span className="text-green-600">Eco</span><span style={{ color: "#6b3424" }}>Mina</span>
            </h1>
          </div>
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md bg-white text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>
        <hr className={`my-2 mx-3 ${darkMode ? "border-light" : "border-gray-300"}`} />

        {/* Sidebar Menu */}
        <ul className="list-unstyled px-2 flex-grow space-y-1">
          {routes
            .filter((route) => route.label)
            .map((route, index) => {
              const restrictedTo = route.restrictedTo || [];
              const isAuthenticated = route.isProtected || localStorage.getItem("authToken");
              const userRole = localStorage.getItem("role");
              const hasAccess = !restrictedTo.includes(userRole);

              // Special check for register company route - hide if company admin has finished form
              const isRegisterCompanyRoute = route.path === '/register-company';
              const shouldHideForCompanyAdmin = isRegisterCompanyRoute && userRole === 'companyAdmin' && isFormFinished;
              
              // Check if company admin hasn't finished registration - disable tabs except profile and register-company
              const isCompanyAdminNotRegistered = userRole === 'companyAdmin' && !isFormFinished;
              const isProfileRoute = route.path === '/profile';
              const shouldDisableTab = isCompanyAdminNotRegistered && !isProfileRoute && !isRegisterCompanyRoute;

              // Special check for emissions-questions route - only show if user has 'Environment' dimension
              const isEmissionsQuestionsRoute = route.path === '/emissions-questions';
              
              // For emissions-questions route, only show if:
              // 1. Dimensions have been loaded, AND
              // 2. User has Environment dimension
              // Otherwise, hide it
              const shouldShowEmissionsQuestions = isEmissionsQuestionsRoute 
                ? (dimensionsLoaded && hasEnvironmentDimension())
                : false;
              
              const shouldHideEmissionsQuestions = isEmissionsQuestionsRoute && !shouldShowEmissionsQuestions;

              if (isAuthenticated && hasAccess && !shouldHideForCompanyAdmin && !shouldHideEmissionsQuestions) {
                return (
                  <li key={index} className="mb-1">
                    {route.path === "/questions-answers" ? (
                      <div className="relative">
                        <button
                          className={`flex items-center px-3 py-2 w-full text-left rounded-lg transition-all duration-300 shadow-sm focus:outline-none text-sm ${
                            shouldDisableTab
                              ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border border-gray-200"
                              : location.pathname === route.path
                              ? darkMode
                                ? "bg-primary text-white"
                                : "bg-green-600 text-white"
                              : darkMode
                              ? "text-light hover:bg-primary hover:text-white"
                              : "text-gray-700 bg-white hover:bg-green-500 hover:text-green-700 border border-gray-200"
                          }`}
                          onClick={() => {
                            if (!shouldDisableTab) {
                              setIsDropdownOpen(!isDropdownOpen);
                            }
                          }}
                          disabled={shouldDisableTab}
                        >
                          <div className="flex items-center flex-1">
                            {route.icon && <span className="mr-3">{route.icon}</span>}
                            <span className="inline">
                              {route.path === '/Home' ? t('navigation.home') :
                               route.path === '/users' ? t('navigation.users') :
                               route.path === '/questions' ? t('navigation.questionsAssignment') :
                               route.path === '/reporting' ? t('navigation.reporting') :
                               route.path === '/faqs' ? t('navigation.faqs') :
                               route.path === '/register-company' ? t('navigation.registerCompany') :
                               route.path === '/questions-management' ? t('navigation.questionsManagement') :
                               route.path === '/questions-answers' ? t('navigation.questionsAnswers') :
                               route.label}
                            </span>
                          </div>
                          <i
                            className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"} text-xs ml-2 transition-transform duration-300`}
                          ></i>
                        </button>

                        {/* Dropdown for Subtopics */}
                        {isDropdownOpen && (
                          <ul className="relative w-full mt-1 p-1 z-10 space-y-1">
                            {loading ? (
                              <li className="text-gray-500 px-3 py-2 text-xs rounded-lg bg-gray-50 text-center">{t('common.loading')}</li>
                            ) : subTopics.length > 0 ? (
                              subTopics.map((subTopic, subIndex) => (
                                <li key={subIndex} className="mb-1">
                                  <button
                                    className="flex items-center px-3 py-2 w-full text-left rounded-lg text-xs transition-all duration-300 bg-gray-50 hover:bg-green-500 hover:text-white text-gray-700 shadow-sm border border-gray-100 hover:border-green-400"
                                    onClick={() => {
                                      navigate(subTopic.path);
                                      handleMobileLinkClick();
                                    }}
                                  >
                                    <i className="bi bi-chevron-right text-xs mr-2"></i>
                                    {subTopic.label}
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li className="text-gray-500 px-3 py-2 text-xs rounded-lg bg-gray-50 text-center">{t('navigation.noTopicsAvailable')}</li>
                            )}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={shouldDisableTab ? '#' : route.path}
                        className={`flex items-center px-3 py-2 rounded-lg transition-all duration-300 no-underline text-sm focus:outline-none ${
                          shouldDisableTab
                            ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border border-gray-200 pointer-events-none"
                            : location.pathname === route.path
                            ? darkMode
                              ? "bg-primary text-white shadow-md"
                              : "bg-green-600 text-white shadow-md"
                            : darkMode
                            ? "text-light hover:bg-secondary hover:text-white"
                            : "text-gray-700 hover:bg-green-500 hover:text-white border border-gray-200"
                        }`}
                        onClick={(e) => {
                          if (shouldDisableTab) {
                            e.preventDefault();
                            return;
                          }
                          handleMobileLinkClick();
                        }}
                      >
                        {route.icon && <span className="mr-3">{route.icon}</span>}
                        <span className="inline">
                          {route.path === '/Home' ? t('navigation.home') :
                           route.path === '/users' ? t('navigation.users') :
                           route.path === '/questions' ? t('navigation.questionsAssignment') :
                           route.path === '/reporting' ? t('navigation.reporting') :
                           route.path === '/faqs' ? t('navigation.faqs') :
                           route.path === '/register-company' ? t('navigation.registerCompany') :
                           route.path === '/questions-management' ? t('navigation.questionsManagement') :
                           route.path === '/questions-answers' ? t('navigation.questionsAnswers') :
                           route.label}
                        </span>
                      </Link>
                    )}
                  </li>
                );
              }
              return null;
            })}
        </ul>

        {/* Profile and Logout */}
        <div className="p-3 border-t border-gray-300 space-y-2">
          <Link
            to="/profile"
            className={`flex items-center px-3 py-2 rounded-lg transition-all duration-300 text-sm no-underline focus:outline-none ${
              darkMode
                ? "text-white hover:bg-gray-700 hover:text-white"
                : "text-gray-700 hover:bg-green-500 hover:text-white border border-gray-200"
            }`}
            onClick={handleMobileLinkClick}
          >
            <i className="bi bi-person mr-3"></i>
            <span className="inline">
              {t('navigation.profile')}
            </span>
          </Link>
          <button
            onClick={() => {
              handleLogout();
              handleMobileLinkClick();
            }}
            className="flex items-center justify-center w-full px-3 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-all duration-300 text-sm focus:outline-none"
          >
            <i className="bi bi-box-arrow-right mr-2"></i>
{t('auth.logout')}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-grow w-full max-w-full overflow-x-hidden transition-all duration-300 ${
        !isMobile ? "md:ml-[240px]" : ""
      }`}>
        {/* Navbar */}
        <Disclosure
          as="nav"
          className={`p-2 ${darkMode ? "bg-secondary text-light" : "bg-white text-gray-400"} shadow-sm border-b border-gray-200`}
        >
          <div className="flex justify-between items-center h-12 px-4 sm:px-6 lg:px-6">
            <div className="flex items-center">
              {/* Mobile Menu Toggle Button */}
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-md bg-white text-gray-500 hover:text-gray-700 focus:outline-none mr-2"
                >
                  <Bars3Icon className="h-6 w-6" />
                </button>
              )}
              
              <div className="flex items-center space-x-3">
                <img src="/EcoMina_Favicon.png" alt="EcoMina Logo" className="h-8 w-8 mr-2 hidden md:inline" />
                {location.pathname === '/Home' && (
                  <div className="relative inline-block">
                    <span
                      className="text-green-700 font-semibold text-xl hidden md:inline"
                    >
{t('navigation.home')}
                    </span>
                  </div>
                )}
                <span className="text-gray-500 text-base ml-3 hidden lg:inline bg-gray-100 px-3 py-1 rounded-full">
                  {t('navigation.yourEsgPortal')}
                </span>
              </div>
            </div>

            {/* Right Section with Clock, Notification, and Profile */}
            <div className="flex items-center space-x-4">
              {/* Analog Clock */}
              <div className="max-sm:hidden relative w-8 h-8 bg-white rounded-full shadow-sm border border-gray-200 p-1">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle cx="50" cy="50" r="45" stroke="#e5e7eb" strokeWidth="3" fill="none" />
                  
                  {/* Clock Hands */}
                  <line 
                    x1="50" y1="50" x2="50" y2="30"
                    stroke="#374151" strokeWidth="3" 
                    strokeLinecap="round"
                    transform={`rotate(${hoursRotation} 50 50)`}
                  />
                  <line 
                    x1="50" y1="50" x2="50" y2="20"
                    stroke="#374151" strokeWidth="2"
                    strokeLinecap="round"
                    transform={`rotate(${minutesRotation} 50 50)`}
                  />
                  <line 
                    x1="50" y1="50" x2="50" y2="15"
                    stroke="#ef4444" strokeWidth="1"
                    strokeLinecap="round"
                    transform={`rotate(${secondsRotation} 50 50)`}
                  />

                  {/* Center Circle */}
                  <circle cx="50" cy="50" r="3" fill="#374151" />
                </svg>
              </div>

              {/* Digital Clock */}
              <div className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-lg">
                {time.toLocaleTimeString()}
              </div>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Notification Icon */}
              <button className="relative p-2 rounded-full bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 shadow-sm border border-gray-200 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500">
                <BellIcon className="h-5 w-5 text-green-700 dark:text-green-500" />
                <span className="absolute top-1 right-1 inline-block w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
              </button>

              {/* Profile Menu */}
              <Menu as="div" className="relative">
                <MenuButton className="rounded-full flex items-center justify-center bg-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-md">
                  <div className={`h-8 w-8 rounded-full ${getAvatarColor(userName)} flex items-center justify-center text-white font-semibold text-xs`}>
                    {getUserInitials(userName)}
                  </div>
                </MenuButton>
                <MenuItems
                  className={`absolute right-0 mt-2 w-60 origin-top-right py-3 shadow-xl bg-white ${
                    darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                  } rounded-xl border border-gray-200 z-[9999]`}
                >
                  <div className="relative border-b border-gray-200 pb-3 text-center px-4">
                    <div className="relative mt-1 flex justify-center">
                      <div className={`h-12 w-12 rounded-full ${getAvatarColor(userName)} flex items-center justify-center text-white font-semibold text-base border-2 border-gray-300 shadow-md -mb-4`}>
                        {getUserInitials(userName)}
                      </div>
                    </div>
                    <div className={`mt-6 text-base font-semibold ${darkMode ? "text-white" : "text-black"}`}>{userName}</div>
                    <div className={`text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{userEmail}</div>
                  </div>
                  <div className="px-2 py-1 space-y-1">
                    <MenuItem>
                      <Link to="/profile" className={`flex items-center px-3 py-2 text-sm rounded-lg focus:outline-none no-underline w-full transition-all duration-300 ${
                        darkMode ? "text-white hover:bg-gray-700" : "text-black hover:bg-gray-100"
                      }`}>
                        <i className="bi bi-person mr-3"></i>
{t('navigation.yourProfile')}
                      </Link>
                    </MenuItem>
                    <MenuItem>
                      <Link to="/faqs" className={`flex items-center px-3 py-2 text-sm rounded-lg focus:outline-none no-underline w-full transition-all duration-300 ${
                        darkMode ? "text-white hover:bg-gray-700" : "text-black hover:bg-gray-100"
                      }`}>
                        <i className="bi bi-question-circle mr-3"></i>
{t('faqs.frequentlyAskedQuestions')}
                      </Link>
                    </MenuItem>
                    <MenuItem>
                      <div onClick={handleLogout} className={`flex items-center px-3 py-2 text-sm rounded-lg focus:outline-none no-underline w-full transition-all duration-300 cursor-pointer font-medium ${
                        darkMode ? "text-white hover:bg-gray-700" : "text-black hover:bg-gray-100"
                      }`}>
                        <i className="bi bi-box-arrow-right mr-3"></i>
{t('auth.logout')}
                      </div>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </Disclosure>
        
        <main className="w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}